import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { extractGuestLinkFromReturnPath, sanitizeReturnPath, buildGuestPresentPath } from '../utils/myEventsUrlParams';
import defaultCteLogo from '../assets/acuity-cte-logo.png';
import './Login.css';

const LEGACY_LOGO_URL = 'https://acuitycte-files.s3.us-west-2.amazonaws.com/public/acuity-tech-logo.png';

const isGlobalAccessCard = (card) => !card || card.global === true || card.school_id == null;

const resolveLogoUrl = (card) => {
  if (isGlobalAccessCard(card)) return defaultCteLogo;
  const schoolLogo = card?.school?.logo_url;
  if (schoolLogo && schoolLogo !== LEGACY_LOGO_URL) return schoolLogo;
  return defaultCteLogo;
};

const VIEWS = {
  LOGIN: 'login',
  FORGOT_REQUEST: 'forgot-request',
  FORGOT_VERIFY: 'forgot-verify',
  FORGOT_RESET: 'forgot-reset',
};

const Login = ({ setUserData, userData }) => {
  const [view, setView] = useState(VIEWS.LOGIN);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [logoUrl, setLogoUrl] = useState(defaultCteLogo);
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from ||
    (new URLSearchParams(location.search).get('returnTo')) ||
    '/';

  useEffect(() => {
    if (userData) {
      navigate(sanitizeReturnPath(from), { replace: true });
    }
  }, [userData, navigate, from]);

  useEffect(() => {
    if (userData) return undefined;

    const returnTo = location.state?.from || new URLSearchParams(location.search).get('returnTo') || '';
    const guestLink = extractGuestLinkFromReturnPath(returnTo);
    if (!guestLink) return undefined;

    navigate(buildGuestPresentPath(guestLink), { replace: true });
  }, [userData, location.state, location.search, navigate]);

  useEffect(() => {
    const defaultAccessCard = userData?.accesscards?.find(
      (ac) => ac.accesscard_id === userData.default_accesscard_id
    );
    setLogoUrl(resolveLogoUrl(defaultAccessCard));
  }, [userData]);

  const resetMessages = () => {
    setError('');
    setInfo('');
  };

  const switchView = (nextView) => {
    resetMessages();
    setView(nextView);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    resetMessages();
    setLoading(true);

    try {
      const response = await axios.post('/api/login', { email, password });
      localStorage.setItem('token', response.data.token);

      const userResponse = await axios.get('/api/user', {
        headers: { Authorization: `Bearer ${response.data.token}` }
      });
      setUserData(userResponse.data);
      navigate(sanitizeReturnPath(from), { replace: true });
    } catch (loginError) {
      if (loginError.response?.status === 403 && loginError.response?.data?.code === 'ACCOUNT_INACTIVE') {
        navigate('/account-inactive');
      } else {
        setError('Invalid email or password');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotRequest = async (e) => {
    e.preventDefault();
    resetMessages();
    setLoading(true);

    try {
      const response = await axios.post('/api/temp-links/forgot-password/request', { email });
      setInfo(response.data.message);
      setVerificationCode('');
      setResetToken('');
      setNewPassword('');
      setConfirmPassword('');
      setView(VIEWS.FORGOT_VERIFY);
    } catch (requestError) {
      setError(requestError.response?.data?.error || 'Unable to send verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotVerify = async (e) => {
    e.preventDefault();
    resetMessages();
    setLoading(true);

    try {
      const response = await axios.post('/api/temp-links/forgot-password/verify', {
        email,
        code: verificationCode.trim(),
      });
      setResetToken(response.data.token);
      setInfo('Code verified. Choose a new password below.');
      setView(VIEWS.FORGOT_RESET);
    } catch (verifyError) {
      setError(verifyError.response?.data?.error || 'Unable to verify code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotReset = async (e) => {
    e.preventDefault();
    resetMessages();

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await axios.post('/api/temp-links/reset-password', {
        token: resetToken,
        newPassword,
      });
      setInfo('Password updated successfully. You can sign in with your new password.');
      setPassword('');
      setVerificationCode('');
      setNewPassword('');
      setConfirmPassword('');
      setResetToken('');
      setView(VIEWS.LOGIN);
    } catch (resetError) {
      setError(resetError.response?.data?.error || 'Unable to reset password. Please request a new code.');
    } finally {
      setLoading(false);
    }
  };

  const renderMessages = () => (
    <>
      {error && <div className="login-message error">{error}</div>}
      {info && <div className={`login-message ${view === VIEWS.LOGIN && info.includes('successfully') ? 'success' : 'info'}`}>{info}</div>}
    </>
  );

  const renderBrand = (title, subtitle) => (
    <div className="login-brand">
      <Link to="/" className="login-brand-link">
        <img src={logoUrl} alt="Acuity Career Technical Education" className="login-brand-logo" />
      </Link>
      <h1 className="login-title">{title}</h1>
      {subtitle && <p className="login-subtitle">{subtitle}</p>}
    </div>
  );

  return (
    <div className="login-page">
      <div className="login-shell">
      <div className="login-card">
        {view === VIEWS.LOGIN && (
          <>
            {renderBrand('Welcome back', 'Sign in to continue to Acuity CTE')}
            {renderMessages()}
            <form onSubmit={handleSubmit}>
              <div className="login-field">
                <label htmlFor="email" className="login-label">Email</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="login-input"
                  autoComplete="email"
                  required
                />
              </div>
              <div className="login-field">
                <label htmlFor="password" className="login-label">Password</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="login-input"
                  autoComplete="current-password"
                  required
                />
              </div>
              <div className="login-actions">
                <button type="submit" className="login-button" disabled={loading}>
                  {loading ? 'Signing in...' : 'Sign in'}
                </button>
              </div>
            </form>
          </>
        )}

        {view === VIEWS.FORGOT_REQUEST && (
          <>
            {renderBrand('Reset password', 'Enter your account email and we will send a verification code.')}
            {renderMessages()}
            <form onSubmit={handleForgotRequest}>
              <div className="login-field">
                <label htmlFor="forgot-email" className="login-label">Email</label>
                <input
                  type="email"
                  id="forgot-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="login-input"
                  autoComplete="email"
                  required
                />
              </div>
              <div className="login-actions">
                <button type="submit" className="login-button" disabled={loading}>
                  {loading ? 'Sending code...' : 'Send verification code'}
                </button>
                <button
                  type="button"
                  className="login-link-button"
                  onClick={() => switchView(VIEWS.LOGIN)}
                  disabled={loading}
                  style={{ alignSelf: 'center' }}
                >
                  Back to sign in
                </button>
              </div>
            </form>
            <p className="login-meta">Codes expire after 5 minutes.</p>
          </>
        )}

        {view === VIEWS.FORGOT_VERIFY && (
          <>
            {renderBrand('Enter verification code', `Check your inbox for a code sent to ${email || 'your email'}.`)}
            {renderMessages()}
            <form onSubmit={handleForgotVerify}>
              <div className="login-field">
                <label htmlFor="verification-code" className="login-label">Verification code</label>
                <input
                  type="text"
                  id="verification-code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="login-input code-input"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="000000"
                  maxLength={6}
                  required
                />
              </div>
              <div className="login-actions">
                <button type="submit" className="login-button" disabled={loading || verificationCode.length !== 6}>
                  {loading ? 'Verifying...' : 'Verify code'}
                </button>
                <div className="login-footer centered">
                  <button
                    type="button"
                    className="login-link-button"
                    onClick={() => switchView(VIEWS.FORGOT_REQUEST)}
                    disabled={loading}
                  >
                    Resend code
                  </button>
                  <button
                    type="button"
                    className="login-link-button"
                    onClick={() => switchView(VIEWS.LOGIN)}
                    disabled={loading}
                  >
                    Back to sign in
                  </button>
                </div>
              </div>
            </form>
            <p className="login-meta">
              Didn&apos;t receive a code? Password reset may not be available for your account, or the code may have expired.
            </p>
          </>
        )}

        {view === VIEWS.FORGOT_RESET && (
          <>
            {renderBrand('Choose a new password', 'Create a new password for your account.')}
            {renderMessages()}
            <form onSubmit={handleForgotReset}>
              <div className="login-field">
                <label htmlFor="new-password" className="login-label">New password</label>
                <input
                  type="password"
                  id="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="login-input"
                  autoComplete="new-password"
                  required
                />
              </div>
              <div className="login-field">
                <label htmlFor="confirm-password" className="login-label">Confirm password</label>
                <input
                  type="password"
                  id="confirm-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="login-input"
                  autoComplete="new-password"
                  required
                />
              </div>
              <div className="login-actions">
                <button type="submit" className="login-button" disabled={loading}>
                  {loading ? 'Updating password...' : 'Update password'}
                </button>
                <button
                  type="button"
                  className="login-link-button"
                  onClick={() => switchView(VIEWS.LOGIN)}
                  disabled={loading}
                  style={{ alignSelf: 'center' }}
                >
                  Back to sign in
                </button>
              </div>
            </form>
          </>
        )}
      </div>

      {view === VIEWS.LOGIN && (
        <div className="login-below-card">
          <button
            type="button"
            className="login-forgot-link"
            onClick={() => switchView(VIEWS.FORGOT_REQUEST)}
          >
            Forgot password?
          </button>
        </div>
      )}
      </div>
    </div>
  );
};

export default Login;
